import { useEffect, useState } from 'react';
import type { LeaderboardData } from '../lib/types';
import { useApi } from '../lib/api';
import { t, tp } from '../lib/i18n';

interface Props {
  campaignId: string;
  refreshIntervalMs?: number;
}

type TabKey = 'daily' | 'weekly' | 'monthly' | 'overall';

export default function Leaderboard({ campaignId, refreshIntervalMs }: Props) {
  const api = useApi();
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [tab, setTab] = useState<TabKey>('overall');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api<LeaderboardData>(`/api/campaigns/${campaignId}/leaderboard`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }
    load();
    if (refreshIntervalMs) {
      const id = setInterval(load, refreshIntervalMs);
      return () => {
        cancelled = true;
        clearInterval(id);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [api, campaignId, refreshIntervalMs]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="muted small">{t('lb.loading')}</div>;

  const rows =
    tab === 'overall'
      ? data.periodicLeaderboards.overall.top
      : data.periodicLeaderboards[tab].top;

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>{t('lb.title')}</h3>
        <span className="small muted">
          {tp('lb.signupsCount', { n: data.totalParticipants })}
        </span>
      </div>

      {!data.topNActive && data.minParticipantsForTopN > 0 && (
        <div className="notice" style={{ marginBottom: '0.75rem' }}>
          {tp('lb.minNeeded', { n: data.remainingForTopN })}
        </div>
      )}

      <div className="tabs">
        <button className={tab === 'daily' ? 'active' : ''} onClick={() => setTab('daily')}>
          {t('lb.tabDaily')}
        </button>
        <button className={tab === 'weekly' ? 'active' : ''} onClick={() => setTab('weekly')}>
          {t('lb.tabWeekly')}
        </button>
        <button className={tab === 'monthly' ? 'active' : ''} onClick={() => setTab('monthly')}>
          {t('lb.tabMonthly')}
        </button>
        <button className={tab === 'overall' ? 'active' : ''} onClick={() => setTab('overall')}>
          {t('lb.tabOverall')}
        </button>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ width: 60 }}>#</th>
            <th>{t('lb.col.name')}</th>
            <th style={{ textAlign: 'right' }}>{t('lb.col.referrals')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={3} className="muted">{t('lb.empty')}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={`${tab}-${r.rank}`}>
              <td>{r.rank}</td>
              <td>{r.displayName}</td>
              <td style={{ textAlign: 'right' }}>
                {r.referralsThisPeriod ?? r.referralCount ?? 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
